/**
 * ApiKeyCard — story-003: API key management
 *
 * Shows the user's API key (masked), lets them generate one if none exists,
 * and supports rotation with a confirmation dialog.
 */
import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useSelector } from 'react-redux';
import config from '../utils/Constants';

// ---------------------------------------------------------------------------
// Types — matched to /api/v1/tenants/api-keys backend schema
// ---------------------------------------------------------------------------

interface ApiKeyEntry {
  id: number;
  name: string | null;
  key_prefix: string;
  tenant_slug: string;
  is_active: boolean;
  created: string;
}

interface ApiKeyCreated {
  id: number;
  name: string | null;
  raw_key: string;  // only returned once on create/rotate
  created: string;
}

const API_KEYS_URL = () => `${config.getDynamicApiUrl()}/api/v1/tenants/api-keys`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function authHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}` };
}

async function fetchKeys(tenantSlug: string): Promise<ApiKeyEntry[]> {
  const headers = await authHeaders();
  const res = await axios.get(`${API_KEYS_URL()}?tenant_slug=${tenantSlug}`, { headers });
  return res.data as ApiKeyEntry[];
}

async function createKey(tenantSlug: string, name: string): Promise<ApiKeyCreated> {
  const headers = await authHeaders();
  const res = await axios.post(API_KEYS_URL(), { tenant_slug: tenantSlug, name }, { headers });
  return res.data as ApiKeyCreated;
}

async function rotateKey(id: number): Promise<ApiKeyCreated> {
  const headers = await authHeaders();
  const res = await axios.post(`${API_KEYS_URL()}/${id}/rotate`, {}, { headers });
  return res.data as ApiKeyCreated;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ApiKeyCard() {
  const selectedTenant = useSelector((state: any) => state.user?.selectedTenant);
  const tenantSlug: string = React.useMemo(() => {
    if (selectedTenant?.slug) return selectedTenant.slug;
    // Fallback: derive from api_base_url (https://{slug}.apps.bacclabs.io)
    try {
      return new URL(selectedTenant?.api_base_url ?? '').hostname.split('.')[0];
    } catch {
      return '';
    }
  }, [selectedTenant]);
  const [keys, setKeys] = React.useState<ApiKeyEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Raw key state — only populated immediately after create/rotate
  const [rawKey, setRawKey] = React.useState<string | null>(null);
  const [rawKeyModalOpen, setRawKeyModalOpen] = React.useState(false);

  // Separate loading flag for the generate action so failure restores the button
  const [generating, setGenerating] = React.useState(false);

  // Rotate confirmation dialog
  const [rotateDialogOpen, setRotateDialogOpen] = React.useState(false);
  const [rotatingKeyId, setRotatingKeyId] = React.useState<number | null>(null);
  const [rotating, setRotating] = React.useState(false);

  // Snackbar for copy feedback
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  // Load keys when tenant is known
  React.useEffect(() => {
    if (!tenantSlug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchKeys(tenantSlug)
      .then(setKeys)
      .catch((err) => setError(err?.message ?? 'Failed to load API keys'))
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  // ---- Generate (create) ----
  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const created = await createKey(tenantSlug, 'Default');
      setRawKey(created.raw_key);
      setRawKeyModalOpen(true);
      const updated = await fetchKeys(tenantSlug);
      setKeys(updated);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  // ---- Rotate ----
  const handleRotateConfirm = async () => {
    if (!rotatingKeyId) return;
    setRotating(true);
    setError(null);
    try {
      const created = await rotateKey(rotatingKeyId);
      setRawKey(created.raw_key);
      setRawKeyModalOpen(true);
      const updated = await fetchKeys(tenantSlug);
      setKeys(updated);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to rotate API key');
    } finally {
      setRotating(false);
      setRotateDialogOpen(false);
      setRotatingKeyId(null);
    }
  };

  // ---- Copy ----
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => setSnackbarOpen(true));
  };

  // ---- Render ----
  const hasKeys = keys.length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <KeyIcon color="primary" />
        <Typography variant="h6" component="h2">
          API Key
        </Typography>
      </Box>

      {loading && !hasKeys && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {!loading && !hasKeys && !error && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No API key yet. Generate one to start using the Cynthia API.
          </Typography>
          <Button variant="contained" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Generating…' : 'Generate API Key'}
          </Button>
        </Box>
      )}

      {hasKeys && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {keys.map((key) => (
            <Box
              key={key.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                size="small"
                value={`${key.key_prefix}${'•'.repeat(28)}`}
                label={key.name ?? 'API Key'}
                InputProps={{ readOnly: true, style: { fontFamily: 'monospace' } }}
                helperText={`Created: ${new Date(key.created).toLocaleDateString()}${!key.is_active ? ' · Inactive' : ''}`}
                sx={{ flexGrow: 1, minWidth: 220 }}
              />
              <Tooltip title="Rotate key">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setRotatingKeyId(key.id);
                    setRotateDialogOpen(true);
                  }}
                  disabled={rotating}
                >
                  Rotate
                </Button>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {/* ---- Raw Key Modal ---- */}
      <Dialog open={rawKeyModalOpen} onClose={() => setRawKeyModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Your API Key</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <strong>Copy this key now — it won't be shown again.</strong>
          </DialogContentText>
          <TextField
            fullWidth
            value={rawKey ?? ''}
            InputProps={{
              readOnly: true,
              style: { fontFamily: 'monospace' },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy to clipboard">
                    <IconButton onClick={() => handleCopy(rawKey ?? '')} edge="end">
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCopy(rawKey ?? '')} startIcon={<ContentCopyIcon />}>
            Copy
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setRawKeyModalOpen(false);
              setRawKey(null);
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Rotate Confirmation Dialog ---- */}
      <Dialog open={rotateDialogOpen} onClose={() => setRotateDialogOpen(false)}>
        <DialogTitle>Rotate API Key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will invalidate your current key immediately. Any integrations using it will stop
            working until updated. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateDialogOpen(false)} disabled={rotating}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleRotateConfirm}
            disabled={rotating}
          >
            {rotating ? <CircularProgress size={18} /> : 'Rotate Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Copy Snackbar ---- */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2500}
        onClose={() => setSnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
